import { useCallback, useEffect, useRef, useState } from "react";
import { AxiosError } from "axios";
import { useQueryClient } from "react-query";
import { useGetWorkflowRunByUUID, useWorkflowLock } from "../api/WorkflowApi";
import { useActorInfoQuery } from "../api/UsersApi";
import type { ReportSummaryItemApi } from "../api/Reports";
import type { WorkflowRunRowDataResponse } from "../api/Workflows";
import { useAppContext } from "../AppProviders";
import { CacheKeys } from "../api/Cache";
import { useStorageMap } from "../storage/Storage";
import { applyReportCacheUpdate } from "../services/websocket/reportCacheUpdates";
import { cacheUpdateEvents } from "../services/websocket/cacheUpdateEvents";
import { threeSixtyAssigneeOverlay } from "../services/websocket/threeSixtyAssigneeOverlay";
import { useApplicationToast } from "../webcontent/ApplicationToastProvider";
import type { ConfirmDialogActions } from "../shared-components/ConfirmationDialog";
import {
  buildClaimedReportUpdate,
  getActorDisplayName,
  is360WorkflowRunUnassigned,
} from "./reportAssignment";

const CLAIM_CONFLICT_TOAST_MESSAGE =
  "This report was just claimed by another investigator.";

type UseWorkbenchClaimPromptArgs = {
  reportId: string;
  reportData: ReportSummaryItemApi | undefined;
  reportDataLoading: boolean;
  /** ``360`` sub-workflow run uuid (same target as list-view Claim); not the parent from ``use_parent`` summary. */
  claimWorkflowRunUuid: string | undefined;
  refreshReport: (reportUuid: string) => void;
};

export function useWorkbenchClaimPrompt({
  reportId,
  reportData,
  reportDataLoading,
  claimWorkflowRunUuid,
  refreshReport,
}: UseWorkbenchClaimPromptArgs) {
  const [isOpen, setIsOpen] = useState(false);
  const declinedReportIdsRef = useRef<Set<string>>(new Set());
  const claimInFlightRef = useRef(false);
  const { mutateAsync: lockWorkflow, isLoading: isClaiming } =
    useWorkflowLock();
  const { data: actorInfo } = useActorInfoQuery(true);
  const { cache } = useAppContext();
  const queryClient = useQueryClient();
  const { updateCacheRefKey } = useStorageMap();
  const { showPushToast } = useApplicationToast();
  const {
    data: workflowRunRow,
    isLoading: workflowRunLoading,
    isFetchedAfterMount: workflowRunReadSinceMount,
    isError: workflowRunError,
  } = useGetWorkflowRunByUUID(claimWorkflowRunUuid, {
    // Offering to claim must never be based on a row cached by an earlier visit or another tab,
    // so re-read the 360 run every time the workbench mounts.
    staleTime: 0,
    refetchOnMount: "always",
  });

  useEffect(() => {
    if (!reportId || reportDataLoading || !reportData) {
      setIsOpen(false);
      return;
    }
    if (reportData.report_uuid !== reportId) {
      setIsOpen(false);
      return;
    }
    if (!claimWorkflowRunUuid) {
      setIsOpen(false);
      return;
    }
    // Wait for the mount re-read to land: cached row data can say unassigned while the run was
    // claimed elsewhere. This stays true once the read completes, so later background refetches
    // (reconnect, invalidation) cannot flap an already-open prompt closed.
    if (workflowRunLoading || !workflowRunReadSinceMount) {
      setIsOpen(false);
      return;
    }
    const overlayAssignee = threeSixtyAssigneeOverlay
      .get(claimWorkflowRunUuid)
      .trim();
    if (overlayAssignee) {
      setIsOpen(false);
      return;
    }
    if (workflowRunError || !workflowRunRow) {
      setIsOpen(false);
      return;
    }
    if (!is360WorkflowRunUnassigned(workflowRunRow)) {
      setIsOpen(false);
      return;
    }
    if (declinedReportIdsRef.current.has(reportId)) {
      setIsOpen(false);
      return;
    }
    setIsOpen(true);
  }, [
    claimWorkflowRunUuid,
    reportId,
    reportData,
    reportDataLoading,
    workflowRunError,
    workflowRunLoading,
    workflowRunReadSinceMount,
    workflowRunRow,
  ]);

  const dismissPrompt = useCallback((id: string) => {
    declinedReportIdsRef.current.add(id);
    setIsOpen(false);
  }, []);

  const applySuccessfulClaimToCaches = useCallback(
    (report: ReportSummaryItemApi, workflowRunUuid: string) => {
      const displayName = getActorDisplayName(actorInfo);
      const actorUuid = actorInfo?.actor_uuid;
      const updateFn = buildClaimedReportUpdate(actorUuid, displayName);
      applyReportCacheUpdate(
        cache.current,
        queryClient,
        report.report_uuid,
        workflowRunUuid,
        updateFn,
        {
          onPersist: () => {
            updateCacheRefKey(CacheKeys.reports, cache.current.reports, false);
          },
          onNotify: (uuid) => cacheUpdateEvents.notify(uuid),
        }
      );
      threeSixtyAssigneeOverlay.set(workflowRunUuid, displayName);
      const existingWorkflowRun =
        queryClient.getQueryData<WorkflowRunRowDataResponse>([
          "workflowRun",
          workflowRunUuid,
        ]);
      if (existingWorkflowRun) {
        queryClient.setQueryData<WorkflowRunRowDataResponse>(
          ["workflowRun", workflowRunUuid],
          {
            ...existingWorkflowRun,
            is_locked: true,
            locked_by: actorUuid ?? existingWorkflowRun.locked_by,
          }
        );
      }
    },
    [actorInfo, cache, queryClient, updateCacheRefKey]
  );

  const handleClaimPromptAction = useCallback(
    async (action: ConfirmDialogActions) => {
      if (action === "close") {
        if (claimInFlightRef.current) {
          return;
        }
        dismissPrompt(reportId);
        return;
      }
      if (
        !reportData ||
        reportData.report_uuid !== reportId ||
        !claimWorkflowRunUuid ||
        claimInFlightRef.current
      ) {
        if (
          reportData?.report_uuid === reportId &&
          !claimWorkflowRunUuid
        ) {
          showPushToast?.({
            message: "Unable to claim this report yet. Please try again.",
            type: "error",
          });
        }
        return;
      }
      claimInFlightRef.current = true;
      try {
        await lockWorkflow({
          workflowRunUUID: claimWorkflowRunUuid,
          origin: "360_claim",
        });
        applySuccessfulClaimToCaches(reportData, claimWorkflowRunUuid);
        refreshReport(reportId);
        void queryClient.invalidateQueries(["workflowRun", claimWorkflowRunUuid]);
        dismissPrompt(reportId);
      } catch (error) {
        const status = (error as AxiosError)?.response?.status;
        if (status === 409) {
          showPushToast?.({
            message: CLAIM_CONFLICT_TOAST_MESSAGE,
            type: "warning",
          });
          refreshReport(reportId);
          void queryClient.invalidateQueries([
            "workflowRun",
            claimWorkflowRunUuid,
          ]);
          dismissPrompt(reportId);
          return;
        }
        showPushToast?.({
          message: "Unable to claim this report. Please try again.",
          type: "error",
        });
      } finally {
        claimInFlightRef.current = false;
      }
    },
    [
      applySuccessfulClaimToCaches,
      claimWorkflowRunUuid,
      dismissPrompt,
      lockWorkflow,
      queryClient,
      reportData,
      reportId,
      refreshReport,
      showPushToast,
    ]
  );

  return {
    isClaimPromptOpen: isOpen,
    isClaiming,
    handleClaimPromptAction,
  };
}
